import {
  Component,
  Element,
  Host,
  h,
  Listen,
  Prop,
  Event,
  EventEmitter,
  Watch,
} from '@stencil/core';
import { carouselNavigationKeyMapper, getNextActiveIndex } from '../../utils/carousel';

export interface AhaImageSelector extends HTMLAhaImageSelectorElement {}

/**
 * Custom image selector component that uses `aha-image-selector-item` as children.
 * @slot default - Contents of the `aha-image-selector`
 */
@Component({
  tag: 'aha-image-selector',
  styleUrl: 'aha-image-selector.css',
  shadow: true,
})
export class AhaImageSelector {
  @Element() host!: HTMLAhaImageSelectorElement;

  /**
   * Selected index
   */
  @Prop({ reflect: true, mutable: true, attribute: 'selected-index' }) selectedIndex: number = -1;

  @Listen('aha-image-selector-item:selected')
  listen(event: CustomEvent<HTMLAhaImageSelectorItemElement>) {
    const selectedItem = event.detail;
    this.selectItem(selectedItem);
  }

  @Watch('selectedIndex')
  private watchSelectedIndex(newIndex: number) {
    this.selectItemByIndex(newIndex);
  }

  /**
   * Triggers before the active index changed
   */
  @Event({ eventName: 'aha-image-selector:active-index-change', cancelable: true, bubbles: true }) //eslint-disable-line @stencil/decorators-style
  indexChange!: EventEmitter<number>;

  /**
   * Triggers after the active index changed
   */
  @Event({ eventName: 'aha-image-selector:active-index-changed', cancelable: true, bubbles: true }) //eslint-disable-line @stencil/decorators-style
  indexChanged!: EventEmitter<number>;

  /**
   * Trigger after the component was connected
   */
  @Event({ eventName: 'aha-image-selector:connected', cancelable: true, bubbles: true }) //eslint-disable-line @stencil/decorators-style
  connected!: EventEmitter<HTMLAhaImageSelectorElement>;

  private get allAhaImageSelectorItem() {
    return Array.from(
      this.host.querySelectorAll('aha-image-selector-item'),
    ) as HTMLAhaImageSelectorItemElement[];
  }

  private selectItem = (selectedItem: HTMLAhaImageSelectorItemElement, emitEvents = true) => {
    const allItems = this.allAhaImageSelectorItem;
    if (emitEvents) this.indexChange.emit(allItems.indexOf(selectedItem));
    allItems.forEach(item => (item.selected = false));
    selectedItem.selected = true;
    this.scrollToSelectedItem(selectedItem);
    if (emitEvents) this.indexChanged.emit(allItems.indexOf(selectedItem));
  };

  private selectItemByIndex = (selectedIndex: number, emitEvents = true) => {
    const allItems = this.allAhaImageSelectorItem;
    if (selectedIndex < 0 || selectedIndex > allItems.length - 1) return;
    const selectedItem = allItems[selectedIndex];
    this.selectItem(selectedItem, emitEvents);
  };

  private scrollToSelectedItem = (selectedItem: HTMLAhaImageSelectorItemElement) => {
    const selectedItemOffset = selectedItem.offsetLeft;
    const spacer = selectedItemOffset > 0 ? -50 : 0;
    this.host.scrollTo({
      left: selectedItemOffset + spacer,
      behavior: 'smooth',
    });
  };

  private handleKeyDown = (event: KeyboardEvent) => {
    if (carouselNavigationKeyMapper[event.key])
      this.selectedIndex = getNextActiveIndex(
        carouselNavigationKeyMapper[event.key],
        this.selectedIndex,
        this.allAhaImageSelectorItem.length,
      );
    this.allAhaImageSelectorItem[this.selectedIndex].focus();
  };

  connectedCallback() {
    this.connected.emit(this.host);
  }

  componentDidLoad() {
    this.selectItemByIndex(this.selectedIndex, false);
  }

  render() {
    return (
      <Host onKeyDown={this.handleKeyDown}>
        <slot></slot>
      </Host>
    );
  }
}
